'use client';

import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { ComponentPropsWithoutRef, useContext, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { CartContext, getApplicableTips } from '@/entities/cart';
import { QueryApi } from '@/features/query/api';
import { cn } from '@/shared/lib/cn';
import { Card } from '@/shared/ui/card';
import { CartTipBoxTipSection } from './cart-tip-box-tip-section';
import './cart-tip-box.scss';

type Props = ComponentPropsWithoutRef<typeof Card>;
export const CartTipBox = (props: Props) => {
    const t = useTranslations();
    const { formMethods } = useContext(CartContext);
    const [paymentMethodId, deliveryMethodId] = useWatch({
        control: formMethods.control,
        name: ['paymentMethod.id', 'deliveryMethod.id'],
    });
    const paymentMethodsQuery = useQuery(QueryApi.PaymentMethods.queries.PaymentMethods);
    const selectedPaymentMethod = paymentMethodsQuery.data?.find((method) => method.id === paymentMethodId);

    const tips = useMemo(
        () => getApplicableTips(selectedPaymentMethod, deliveryMethodId),
        [selectedPaymentMethod, deliveryMethodId],
    );

    if (!tips.length) {
        return null;
    }

    return (
        <Card {...props} className={cn('cart-tip-box', props.className)} data-testid="cart-tip-box">
            <h3 className="cart-tip-box__title" data-testid="cart-tip-box-title">
                {t('CART_TIP_BOX.TITLE')}
            </h3>
            {tips.map((tip) => (
                <CartTipBoxTipSection key={tip.id} tip={tip} />
            ))}
        </Card>
    );
};
